import { defineFailures, failureCodes } from "@amtana/module";
import { z } from "zod";

// An analysis is the core extraction job: pick a platform, pick what to extract
// on it (the mode), pick the specific target, configure, run. Credits are spent
// per profile examined that has data, at the platform's cost.

export const platformSchema = z.enum(["instagram", "twitter", "google-maps", "tiktok", "linkedin"]);
export type Platform = z.infer<typeof platformSchema>;

export const modeSchema = z.enum([
  "account-followers",
  "account-following",
  "account-audience",
  "account-likers",
  "account-commenters",
  "keyword",
  "post-comments",
  "post-likes",
  "hashtag",
  "location",
  "twitter-followers",
  "twitter-following",
  "twitter-replies",
  "twitter-mentions",
  "twitter-keyword",
  "tweet-replies",
  "tweet-retweets",
  "tweet-quotes",
  "maps-search",
]);
export type Mode = z.infer<typeof modeSchema>;

export const targetKindSchema = z.enum(["account", "post", "hashtag", "location", "keyword", "maps"]);
export type TargetKind = z.infer<typeof targetKindSchema>;

export type PlatformInfo = {
  readonly id: Platform;
  readonly name: string;
  readonly description: string;
  readonly creditsPerProspect: number;
  readonly recommended: boolean;
  /** A real provider reads this platform. Only Instagram's does today (HikerAPI); X, Google Maps,
   * TikTok and LinkedIn have none, so they are offered as coming soon and an analysis on them
   * never starts. */
  readonly available: boolean;
};

export const PLATFORMS: readonly PlatformInfo[] = [
  {
    id: "instagram",
    name: "Instagram",
    description: "Extract who follows your competitors' accounts, and who they follow.",
    creditsPerProspect: 1,
    recommended: true,
    available: true,
  },
  {
    id: "twitter",
    name: "Twitter / X",
    description: "Extract the followers of accounts in your niche, and whoever interacts with them.",
    creditsPerProspect: 2,
    recommended: false,
    available: false,
  },
  {
    id: "google-maps",
    name: "Google Maps",
    description: "Extract local businesses, filtered by geographic area and business category.",
    creditsPerProspect: 5,
    recommended: false,
    available: false,
  },
  {
    id: "tiktok",
    name: "TikTok",
    description: "Extract who follows, comments on or interacts with accounts and videos in your niche.",
    creditsPerProspect: 2,
    recommended: false,
    available: false,
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    description: "Extract the people and companies who follow or engage with pages in your niche.",
    creditsPerProspect: 3,
    recommended: false,
    available: false,
  },
];

/** Whether an analysis may start on this platform: a real provider reads it. */
export function isPlatformAvailable(platform: Platform): boolean {
  return PLATFORMS.some((info) => info.id === platform && info.available);
}

export type ModeInfo = {
  readonly id: Mode;
  readonly platform: Platform;
  readonly title: string;
  readonly description: string;
  readonly target: TargetKind;
  readonly isNew?: boolean;
  /** A real provider reads this mode. HikerAPI serves an Instagram account's followers and
   * following; every other mode has none, so it is offered as coming soon, a search or a setup
   * in it is refused, and an analysis in it never starts (D1155). */
  readonly available: boolean;
};

export const MODES: readonly ModeInfo[] = [
  {
    id: "account-followers",
    platform: "instagram",
    title: "Account followers",
    description: "Get the followers of an account",
    target: "account",
    available: true,
  },
  {
    id: "account-following",
    platform: "instagram",
    title: "Account following",
    description: "Get the people an account follows",
    target: "account",
    available: true,
  },
  {
    id: "account-audience",
    platform: "instagram",
    title: "Account audience",
    description: "Get the people who interacted with the account",
    target: "account",
    isNew: true,
    available: false,
  },
  {
    id: "account-likers",
    platform: "instagram",
    title: "Account likes",
    description: "Get the people who like an account's posts",
    target: "account",
    available: false,
  },
  {
    id: "account-commenters",
    platform: "instagram",
    title: "Account comments",
    description: "Get the people who comment on an account's posts",
    target: "account",
    available: false,
  },
  {
    id: "keyword",
    platform: "instagram",
    title: "Keyword",
    description: "Get the creators posting reels about a topic",
    target: "keyword",
    isNew: true,
    available: false,
  },
  {
    id: "post-comments",
    platform: "instagram",
    title: "Post comments",
    description: "Get the people who commented on a post",
    target: "post",
    available: false,
  },
  {
    id: "post-likes",
    platform: "instagram",
    title: "Post likes",
    description: "Get all the users who liked a post",
    target: "post",
    available: false,
  },
  {
    id: "hashtag",
    platform: "instagram",
    title: "Hashtags",
    description: "Get the people posting with one or more hashtags",
    target: "hashtag",
    available: false,
  },
  {
    id: "location",
    platform: "instagram",
    title: "Location",
    description: "Get the people posting from a location",
    target: "location",
    available: false,
  },
  {
    id: "twitter-followers",
    platform: "twitter",
    title: "Account followers",
    description: "Get the followers of an account",
    target: "account",
    available: false,
  },
  {
    id: "twitter-following",
    platform: "twitter",
    title: "Account following",
    description: "Get the people an account follows",
    target: "account",
    available: false,
  },
  {
    id: "twitter-replies",
    platform: "twitter",
    title: "Account replies",
    description: "Get the people replying to an account's tweets",
    target: "account",
    available: false,
  },
  {
    id: "twitter-mentions",
    platform: "twitter",
    title: "Mentions",
    description: "Get the people who mention an account",
    target: "account",
    available: false,
  },
  {
    id: "twitter-keyword",
    platform: "twitter",
    title: "Keyword",
    description: "Get the people tweeting about a topic or hashtag",
    target: "keyword",
    available: false,
  },
  {
    id: "tweet-replies",
    platform: "twitter",
    title: "Tweet replies",
    description: "Get the people who replied to a tweet",
    target: "post",
    available: false,
  },
  {
    id: "tweet-retweets",
    platform: "twitter",
    title: "Retweets",
    description: "Get the people who retweeted a tweet",
    target: "post",
    available: false,
  },
  {
    id: "tweet-quotes",
    platform: "twitter",
    title: "Quotes",
    description: "Get the people who quoted a tweet",
    target: "post",
    available: false,
  },
  {
    id: "maps-search",
    platform: "google-maps",
    title: "Business search",
    description: "Find businesses in any area on Google Maps",
    target: "maps",
    available: false,
  },
];

/** Whether an analysis may run in this mode: a real provider reads it. */
export function isModeAvailable(mode: Mode): boolean {
  return MODES.some((info) => info.id === mode && info.available);
}

export const accountTargetSchema = z.object({
  kind: z.literal("account"),
  username: z.string().min(1),
  displayName: z.string(),
  followers: z.number().int().nonnegative(),
  biography: z.string(),
  verified: z.boolean(),
  /** What the account's audience is about; colours the simulated leads. */
  niche: z.string(),
});

export const postTargetSchema = z.object({
  kind: z.literal("post"),
  url: z.url(),
  ownerUsername: z.string(),
  interactions: z.number().int().nonnegative(),
  niche: z.string(),
});

export const hashtagTargetSchema = z.object({
  kind: z.literal("hashtag"),
  tags: z.array(z.string().min(1)).min(1).max(10),
  posts: z.number().int().nonnegative(),
  niche: z.string(),
});

export const locationTargetSchema = z.object({
  kind: z.literal("location"),
  name: z.string().min(1),
  posts: z.number().int().nonnegative(),
  niche: z.string(),
});

export const keywordTargetSchema = z.object({
  kind: z.literal("keyword"),
  query: z.string().min(2),
  creators: z.number().int().nonnegative(),
  niche: z.string(),
});

export const mapsTargetSchema = z.object({
  kind: z.literal("maps"),
  query: z.string().min(2),
  area: z.string().min(2),
  radiusKm: z.number().int().min(1).max(50),
  businesses: z.number().int().nonnegative(),
  niche: z.string(),
});

export const targetSchema = z.discriminatedUnion("kind", [
  accountTargetSchema,
  postTargetSchema,
  hashtagTargetSchema,
  locationTargetSchema,
  keywordTargetSchema,
  mapsTargetSchema,
]);
export type Target = z.infer<typeof targetSchema>;

/** "Out of credits" is its own status: the run stopped, but nothing it found is lost. */
export const statusSchema = z.enum([
  "not-started",
  "running",
  "paused",
  "completed",
  "out-of-credits",
  "error",
  "deprecated",
]);
export type AnalysisStatus = z.infer<typeof statusSchema>;

export const pauseReasonSchema = z.enum(["manual", "credit-limit", "stopped-for-another"]);

/**
 * Why a run stopped (packages/module ADR-001, revision 1): the catalogue both runtimes raise
 * from, generated into the Go worker. Never prose: the provider's words go to the log only.
 */
export const RUN_FAILURES = defineFailures({
  "provider-refused": { fault: "provider", status: 503 },
  "provider-balance": { fault: "provider", status: 503 },
  "provider-invalid": { fault: "customer", status: 422 },
  "provider-unavailable": { fault: "provider", status: 503 },
  "target-missing": { fault: "customer", status: 404 },
  "list-gone": { fault: "customer", status: 404 },
  unknown: { fault: "platform", status: 503 },
});
/** What a run that failed before codes existed, or with a code this build does not know, reads as. */
export const RUN_FAILURE_FALLBACK = "unknown";
export const runFailureCodeSchema = z.enum(failureCodes(RUN_FAILURES));
export type RunFailureCode = z.infer<typeof runFailureCodeSchema>;

/**
 * Why a page or a batch was given up, as the scan's own rules record it (packages/module ADR-001,
 * revision 1): a code, generated into the worker as a closed type, never prose. Only the scan
 * reads it, so it has no copy; a fact from before codes carries free text that nothing reads.
 */
export const batchFailureReasonSchema = z.enum(["provider-missing", "no-answer", "retries-spent"]);
export type BatchFailureReason = z.infer<typeof batchFailureReasonSchema>;

/** Why a running scan is waiting: on the platform's data provider, or held by us because its
 * work was parked after failing again and again (`run-stalled`, eventing ADR-001 revision 1). */
export const waitingCodeSchema = z.enum([
  "provider-down",
  "provider-slow",
  "provider-refused",
  "run-stalled",
]);
export type WaitingCode = z.infer<typeof waitingCodeSchema>;

export const settingsSchema = z.object({
  /** Pause automatically after spending this many credits; null means no limit. */
  creditLimit: z.number().int().positive().nullable(),
  autoEnrich: z.boolean(),
});
export type AnalysisSettings = z.infer<typeof settingsSchema>;

export const progressSchema = z.object({
  /** The size of the target's audience, capped by the plan's profiles per analysis. */
  totalUsers: z.number().int().nonnegative(),
  profilesAnalyzed: z.number().int().nonnegative(),
  publicAccounts: z.number().int().nonnegative(),
  privateAccounts: z.number().int().nonnegative(),
  usedCredits: z.number().int().nonnegative(),
  emails: z.number().int().nonnegative(),
  phones: z.number().int().nonnegative(),
  /** 0–100. */
  completion: z.number().min(0).max(100),
});
export type AnalysisProgress = z.infer<typeof progressSchema>;

export const analysisSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  platform: platformSchema.nullable(),
  mode: modeSchema.nullable(),
  target: targetSchema.nullable(),
  wizardStep: z.number().int().min(1).max(3),
  status: statusSchema,
  pauseReason: pauseReasonSchema.nullable(),
  settings: settingsSchema,
  progress: progressSchema,
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  /** Why the run stopped; null unless the status is "error". The browser has copy for each code. */
  failure: runFailureCodeSchema.nullable().default(null),
  /** While a run waits on its platform's provider: why, on which platform, and when it tries again. */
  waiting: z
    .object({ code: waitingCodeSchema, platform: platformSchema, retryAt: z.iso.datetime().nullable() })
    .nullable()
    .default(null),
});
export type Analysis = z.infer<typeof analysisSchema>;

export const analysisListSchema = z.object({
  items: z.array(analysisSchema),
  counts: z.record(statusSchema, z.number().int().nonnegative()),
});
export type AnalysisList = z.infer<typeof analysisListSchema>;

/** The home's latest analyses (S39.01): the newest `limit` of the current project, no counts. */
export const recentAnalysesInputSchema = z.object({ limit: z.number().int().min(1).max(20).default(5) });
export type RecentAnalysesInput = z.input<typeof recentAnalysesInputSchema>;

export const recentAnalysesSchema = z.object({ items: z.array(analysisSchema) });
export type RecentAnalyses = z.infer<typeof recentAnalysesSchema>;

export const emptyInputSchema = z.object({});

/** `AnalysisApi.firstCompletions` (ADR-028 revision 1 point 2, S33.13): at most 100
 * organizations a call, the keyset page size of ADR-033; more is a validation error. */
export const firstCompletionsInputSchema = z.object({
  organizationIds: z.array(z.string().min(1)).max(100),
});
export type FirstCompletionsInput = z.infer<typeof firstCompletionsInputSchema>;
/** When each organization's first analysis completed (ISO), by id; one with none is absent. */
export type FirstCompletions = Readonly<Record<string, string>>;

export const analysisIdInputSchema = z.object({ analysisId: z.string() });

export const updateWizardInputSchema = z.object({
  analysisId: z.string(),
  platform: platformSchema.optional(),
  mode: modeSchema.optional(),
  target: targetSchema.nullable().optional(),
  wizardStep: z.number().int().min(1).max(3).optional(),
});
export type UpdateWizardInput = z.infer<typeof updateWizardInputSchema>;

export const searchTargetsInputSchema = z.object({
  mode: modeSchema,
  query: z.string().trim().min(1).max(120),
  /** Google Maps only: where to search. */
  area: z.string().trim().max(120).optional(),
  radiusKm: z.number().int().min(1).max(50).optional(),
});
export type SearchTargetsInput = z.infer<typeof searchTargetsInputSchema>;

export const targetListSchema = z.object({ targets: z.array(targetSchema) });
export type TargetList = z.infer<typeof targetListSchema>;

export const startInputSchema = z.object({
  analysisId: z.string(),
  /** Stop whatever else is running to free the slot. */
  stopOthers: z.boolean().default(false),
});
export type StartInput = z.input<typeof startInputSchema>;

export const renameInputSchema = z.object({ analysisId: z.string(), name: z.string().trim().min(1).max(80) });
export type RenameInput = z.infer<typeof renameInputSchema>;

export const removeInputSchema = z.object({ analysisIds: z.array(z.string()).min(1) });
export type RemoveInput = z.infer<typeof removeInputSchema>;

export const updateSettingsInputSchema = z.object({ analysisId: z.string(), settings: settingsSchema });
export type UpdateSettingsInput = z.infer<typeof updateSettingsInputSchema>;

export const moveInputSchema = z.object({ analysisIds: z.array(z.string()).min(1), projectId: z.string() });
export type MoveInput = z.infer<typeof moveInputSchema>;

export const countSchema = z.object({ changed: z.number().int().nonnegative() });
export type Count = z.infer<typeof countSchema>;
